import clsx from "clsx";
import * as React from "react";
import type { DataIntegration } from "../api/endpoint.js";
import { ProviderIcon } from "./provider-icon.js";
import { isKnownProviderIconSlug } from "./provider-icon-slugs.js";

interface DataIntegrationIconProps {
  integration: DataIntegration;
  size?: "1" | "2" | "3" | "4";
}

type CustomIconStyle = React.CSSProperties & {
  "--woswidgets-provider-icon-light": string;
  "--woswidgets-provider-icon-dark"?: string;
};

const sanitizeIconUrl = (url: string): string | undefined => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === "https:" ? parsedUrl.href : undefined;
  } catch {
    return undefined;
  }
};

const DataIntegrationIcon: React.FC<DataIntegrationIconProps> = ({
  integration,
  size = "4",
}) => {
  const { iconSlug, iconUrl, iconDarkUrl, integrationType } = integration;

  if (iconSlug && isKnownProviderIconSlug(iconSlug)) {
    return <ProviderIcon provider={iconSlug} size={size} />;
  }

  const safeLight = iconUrl ? sanitizeIconUrl(iconUrl) : undefined;
  const safeDark = iconDarkUrl ? sanitizeIconUrl(iconDarkUrl) : undefined;

  if (safeLight || safeDark) {
    const lightUrl = safeLight ?? safeDark;
    const style: CustomIconStyle = {
      "--woswidgets-provider-icon-light": `url("${lightUrl}")`,
      ...(safeDark
        ? { "--woswidgets-provider-icon-dark": `url("${safeDark}")` }
        : {}),
    };
    return (
      <div
        data-custom-icon=""
        className={clsx("woswidgets-provider-icon", `size-${size}`)}
        style={style}
      />
    );
  }

  return <ProviderIcon provider={integrationType} size={size} />;
};

export { DataIntegrationIcon };
export type { DataIntegrationIconProps };
